import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const errMsg = (e) => e.response?.data?.detail?.message || e.response?.data?.detail || 'Operazione non riuscita';

const GrantRow = ({ g, onRevoke, onAction }) => (
  <div className="flex flex-wrap items-center gap-3 py-2 border-b border-border text-xs font-sans" data-testid={`grant-row-${g.grant_id}`}>
    <span className="font-mono">{g.user_id}</span>
    <span>{g.email_masked}</span>
    <Badge className="text-[10px]">{g.level}</Badge>
    <Badge variant={g.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">{g.status}</Badge>
    <span className="text-muted-foreground">{(g.granted_at || '').slice(0, 16)} · {g.reason}</span>
    {g.status === 'active' && <div className="flex gap-1.5 ml-auto">
      <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={() => onAction(g, 'sessions/revoke', 'Chiudere tutte le sessioni di')} data-testid={`grant-sessions-${g.grant_id}`}>Chiudi sessioni</Button>
      <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={() => onAction(g, 'mfa/reset', 'Azzerare il codice TOTP di')} data-testid={`grant-mfa-reset-${g.grant_id}`}>Azzera TOTP</Button>
      <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={() => onRevoke(g)} data-testid={`grant-revoke-${g.grant_id}`}>Revoca</Button>
    </div>}
  </div>
);

export const AdminGrantsTab = () => {
  const [data, setData] = useState({ grants: [], legacy_role_admin: [] });
  const [form, setForm] = useState({ user_id: '', level: 'admin_ops', reason: '' });
  const load = useCallback(() => axios.get(`${API}/admin/grants`, opts).then(r => setData(r.data)).catch(e => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await axios.post(`${API}/admin/grants`, form, opts); toast.success('Permesso assegnato'); setForm({ user_id: '', level: 'admin_ops', reason: '' }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (g) => {
    const reason = window.prompt(`Motivo della revoca per ${g.user_id}:`);
    if (!reason) return;
    try { await axios.post(`${API}/admin/grants/${g.grant_id}/revoke`, { reason }, opts); toast.success('Permesso revocato'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const action = async (g, path, label) => {
    if (!window.confirm(`${label} ${g.user_id}?`)) return;
    try { await axios.post(`${API}/admin/users/${g.user_id}/${path}`, {}, opts); toast.success('Fatto'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className="border border-border rounded-xl" data-testid="admin-grants-tab"><CardContent className="p-5 space-y-5">
      <div className="flex flex-wrap gap-2 items-end">
        <Input placeholder="user_id (es. user_xxxxxxxx)" value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value.trim() })} className="h-9 w-56 text-sm" data-testid="grant-user-id" />
        <Select value={form.level} onValueChange={v => setForm({ ...form, level: v })}>
          <SelectTrigger className="h-9 w-40 text-sm" data-testid="grant-level"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="admin_ops">admin_ops</SelectItem><SelectItem value="admin_super">admin_super</SelectItem></SelectContent>
        </Select>
        <Input placeholder="Motivazione (obbligatoria)" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="h-9 flex-1 min-w-[200px] text-sm" data-testid="grant-reason" />
        <Button onClick={add} disabled={!form.user_id || form.reason.trim().length < 3} className="rounded-full h-9" data-testid="grant-submit">Assegna</Button>
      </div>
      <div>{data.grants.map(g => <GrantRow key={g.grant_id} g={g} onRevoke={revoke} onAction={action} />)}
        {!data.grants.length && <p className="text-xs text-muted-foreground font-sans">Nessun permesso assegnato.</p>}</div>
      {data.legacy_role_admin.length > 0 && (
        <p className="text-xs text-amber-700 font-sans" data-testid="legacy-admin-note">
          Accesso transitorio (ruolo admin storico): {data.legacy_role_admin.map(u => u.user_id).join(', ')}. Verrà rimosso con approvazione del titolare.
        </p>
      )}
    </CardContent></Card>
  );
};
